import { Flex, Text, useMantineTheme } from '@mantine/core';
import type { MantineColor } from '@mantine/core';
import type { ReactNode } from 'react';
import type { SettingsRowCardTone } from '@dealstack/frontend/shared/components/SettingsRowCard/SettingsRowCard.types.ts';

interface Props {
	title: string;
	description: string;
	action?: ReactNode;
	footer?: ReactNode;
	tone?: SettingsRowCardTone;
	withBorder?: boolean;
	'data-testid'?: string;
}

// Figma's Grey/4 (#D3D8DA) and Red/4 (#F76E80) are index 4 of the theme's gray/red scales.
const TONE_PALETTE: Record<SettingsRowCardTone, MantineColor> = {
	neutral: 'gray',
	danger: 'red',
};
const TONE_SHADE = 4;

// Shared settings row card matching the Figma Deal-settings cards (file UlvSXo0qOSeByy4pjrMrCq —
// "Nominee Structure" 32239:184294, "Delete deal" 32242:187608): a 1px-bordered, 4px-radius,
// 16px-padded row with a 14/600 Dark/8 title stacked over a 14/400 Dark/0 description, and an
// action (switch or button) vertically centred on the right. Both Figma cards are the same shape
// and differ only in border colour, hence `tone`. Spacing and typography below are the Figma
// design tokens (Sans/B6●H6 (14), Dark/8, Dark/0) — check a pixel change against those nodes.
//
// `withBorder={false}` drops the chrome and keeps only the typography, for callers that already
// supply their own container (e.g. the fund drawer, where the card sits under a Divider).
export const SettingsRowCard = ({
	title,
	description,
	action,
	footer,
	tone = 'neutral',
	withBorder = true,
	'data-testid': dataTestId,
}: Props) => {
	const theme = useMantineTheme();
	const borderColour = theme.colors[TONE_PALETTE[tone]][TONE_SHADE];

	return (
		<Flex
			direction="column"
			gap="0.5rem"
			p={withBorder ? '1rem' : undefined}
			style={
				withBorder
					? { border: `1px solid ${borderColour}`, borderRadius: theme.radius.sm }
					: undefined
			}
			data-testid={dataTestId}
		>
			<Flex align="center" gap="0.5rem">
				{/* minWidth 0 lets a long description wrap instead of forcing the row wider. */}
				<Flex direction="column" gap="0.5rem" style={{ flex: 1, minWidth: 0 }}>
					<Text size="B6Bold" c="dark.8">
						{title}
					</Text>
					<Text size="B6Medium" c="dark.0">
						{description}
					</Text>
				</Flex>
				{action}
			</Flex>
			{footer}
		</Flex>
	);
};
